/**
 * A drop-in replacement for the `local-storage` package.
 *
 * Browsers throw a `SecurityError` from `window.localStorage` when they deny
 * storage access: Safari with "Prevent cross-site tracking", a third-party
 * iframe, an in-app browser, or cookies switched off. The `local-storage`
 * package reads `window.localStorage` while its own module evaluates, so a bare
 * import of it crashes every module that pulls it in, however far away that
 * import sits (Sentry MUSICAPI-WEB-8R / MUSICAPI-WEB-8T, WEB-1MH / WEB-1GQ).
 *
 * This module touches the storage only inside a call, and only inside a
 * `try`/`catch`. A browser that denies storage loses persistence and keeps
 * working. It is also safe on the server, where there is no `window`.
 */

const readBackend = (): Storage | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.localStorage ?? null;
  } catch {
    // Storage access denied. The caller works without it.
    return null;
  }
};

const parse = <T>(raw: string | null): T | null => {
  if (raw === null) {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    // The `local-storage` package returns values it cannot parse as-is.
    return raw as unknown as T;
  }
};

export const get = <T>(key: string): T | null => {
  const backend = readBackend();
  if (!backend) {
    return null;
  }
  try {
    return parse<T>(backend.getItem(key));
  } catch {
    return null;
  }
};

export const set = <T>(key: string, value: T): boolean => {
  const backend = readBackend();
  if (!backend) {
    return false;
  }
  try {
    backend.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // A full or read-only storage must not break the page.
    return false;
  }
};

export const remove = (key: string): void => {
  const backend = readBackend();
  if (!backend) {
    return;
  }
  try {
    backend.removeItem(key);
  } catch {
    // Nothing to clean up.
  }
};

export const clear = (): void => {
  const backend = readBackend();
  if (!backend) {
    return;
  }
  try {
    backend.clear();
  } catch {
    // Nothing to clean up.
  }
};
